// When the API answers 429, every poller should back off for the time it asked
// for instead of firing its next tick into the same wall. One module-level
// clock, set by the axios response interceptor (auth.ts) and read by the
// pollers: a poll that finds it paused skips that tick. Nothing is retried —
// the next tick after the pause is the retry.

const DEFAULT_PAUSE_SECONDS = 10;
// Whatever the server asks for, the pollers are back within a minute.
const MAX_PAUSE_SECONDS = 60;

let pausedUntil = 0;

type ResponseLike = {
  headers?: Record<string, unknown>;
  data?: unknown;
};

const positiveSeconds = (value: unknown): number | null => {
  const seconds = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
};

/** True while a 429 is still holding the pollers back. */
export function isRateLimited(): boolean {
  return Date.now() < pausedUntil;
}

/**
 * Records the pause a 429 asked for: Retry-After (seconds) from the header,
 * else the body's `retry_after` — the header is only readable cross-origin when
 * the server exposes it — else 10 s, and never more than 60 s. A shorter answer
 * never shortens a pause that is already running.
 *
 * Only the API rate limit counts. Other 429s (the concurrent-pipeline cap, a
 * "render busy" answer) carry their own `error` and say nothing about the
 * request budget.
 */
export function pauseForRateLimit(response: ResponseLike | undefined): void {
  const body = response?.data;
  const code = body && typeof body === 'object' ? (body as { error?: unknown }).error : undefined;
  if (typeof code === 'string' && code !== 'rate_limited') return;

  const seconds =
    positiveSeconds(response?.headers?.['retry-after']) ??
    positiveSeconds((body as { retry_after?: unknown } | undefined)?.retry_after) ??
    DEFAULT_PAUSE_SECONDS;
  pausedUntil = Math.max(pausedUntil, Date.now() + Math.min(seconds, MAX_PAUSE_SECONDS) * 1000);
}

/** Clears the pause. For tests; a real pause simply runs out. */
export function resetRateLimitPause(): void {
  pausedUntil = 0;
}
